/**
 * SportsTournamentsList — /sports/tournaments
 *
 * Browsable directory with sport / location filters.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trophy, MapPin, Calendar, PlusCircle, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import SportsCategoryNav from "@/components/SportsCategoryNav";

export default function SportsTournamentsList() {
  const [params, setParams] = useSearchParams();
  const sport   = params.get("sport") || "";
  const country = params.get("country") || "";
  const city    = params.get("city") || "";
  const q       = params.get("q") || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => { http.get("/sports/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (sport) p.sport = sport;
    if (country) p.country = country;
    if (city) p.city = city;
    if (q) p.q = q;
    http.get("/sports/tournaments", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [sport, country, city, q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <>
      <SportsCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="tournaments-list-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">TOURNAMENTS</div>
            <h1 className="font-display text-4xl mb-1">
              {sport ? `${sport} tournaments` : "All tournaments"}
            </h1>
            <p className="opacity-70">{total} listed{country ? ` in ${country}` : ""}</p>
          </div>
          <Link to="/sports/tournaments/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-tournament">
            <PlusCircle className="w-4 h-4" /> New tournament
          </Link>
        </div>

        {/* Filters */}
        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2 items-center">
          <select value={sport} onChange={e => setFilter("sport", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-sport">
            <option value="">All sports</option>
            {meta?.categories?.map(cat => (
              <optgroup key={cat.key} label={cat.label}>
                {cat.sports.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </optgroup>
            ))}
          </select>
          <input value={country} onChange={e => setFilter("country", e.target.value)}
            placeholder="Country" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-country" />
          <input value={city} onChange={e => setFilter("city", e.target.value)}
            placeholder="City" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-city" />
          <input value={q} onChange={e => setFilter("q", e.target.value)}
            placeholder="Search…" className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]"
            style={{ borderColor: "var(--wofy-border)" }} data-testid="filter-q" />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <p className="opacity-70 mb-3">No tournaments match. Be the first to add one.</p>
            <Link to="/sports/tournaments/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Create tournament
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(t => (
              <Link key={t.id} to={`/sports/tournaments/${t.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`tournament-card-${t.slug}`}>
                {t.cover_image_url && (
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3 bg-black/5"
                       style={{ backgroundImage: `url(${t.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                )}
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
                  <Trophy className="w-3 h-3 inline mr-1" />{t.sport}{t.format ? ` · ${t.format}` : ""}
                </div>
                <div className="font-medium mb-1 line-clamp-2">{t.name}</div>
                <div className="text-xs opacity-70 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />{t.start_date || "TBA"}
                </div>
                {(t.city || t.country) && (
                  <div className="text-xs opacity-70 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{[t.city, t.state, t.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
